import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Breadcrumb, BackdropCircular } from 'components';
import { Formik } from 'formik';
import {
  Badge,
  Card,
  Typography,
  Divider,
  Grid,
  Switch,
  TextField,
  Icon,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
} from '@material-ui/core';
import {
  getHomepageTestimonialsConfigsApi,
  saveHomepageTestimonialsConfigsApi
} from 'api';
import { validationSchema } from './TestimonialsValidationSchema';


const testimonialsGroup = ['first', 'second', 'third', 'fourth', 'fifth'];

const Testimonials = () => {
  const dispatch = useDispatch();
  const {
    homepageTestimonialsConfigs = [],
    loadingHomepageTestimonialsConfigs,
    savingHomepageTestimonialsConfigs
  } = useSelector((state) => state.store_configs);

  const loading = loadingHomepageTestimonialsConfigs ||
    savingHomepageTestimonialsConfigs;

  useEffect(() => {
    if (!homepageTestimonialsConfigs.general.length) {
      getHomepageTestimonialsConfigsApi({dispatch});
    }
  }, []);

  const handleSave = (values) => {
    const configs = Object.values(values).reduce((acc, value) => {
      for (const key in value) {
        acc = [
          ...acc,
          {
            path: key,
            value: value[key]
          }
        ];
      }

      return acc;
    }, []);

    saveHomepageTestimonialsConfigsApi({dispatch, configs});
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <Breadcrumb
        routeSegments={[
          { name: 'Settings', path: '/settings#go_live_requirements' },
          { name: 'UI Configurations', path: '/settings#UI_configurations' },
          { name: 'Homepage Settings', path: '/configurations/homepage'},
          { name: 'Testimonials'}
        ]}
      />
      {!!Object.keys(homepageTestimonialsConfigs['general']).length &&
        <Formik
          onSubmit={(values) => handleSave(values)}
          initialValues={homepageTestimonialsConfigs}
          validationSchema={validationSchema}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            handleChange,
            handleSubmit,
            handleBlur
          }) => (
            <form autoComplete="off" className="p-8" onSubmit={handleSubmit}>
              <Card className="p-8">
                <Typography variant="h4" className="mb-8">Testimonials</Typography>
                <Divider className="mb-8" />
                <Grid container spacing={3} className="mb-8">
                  <Grid item xs={12} sm={2} className="my-2 ml-4">Enable</Grid>
                  <Grid item xs={12} sm={9}>
                    <Switch
                      color="primary"
                      name={'general[store_info/premium_plus_testimonails/is_testimonails_enabled]'}
                      checked={values.general['store_info/premium_plus_testimonails/is_testimonails_enabled'] == 1}
                      onChange={(event) => setFieldValue(
                        'general[store_info/premium_plus_testimonails/is_testimonails_enabled]',
                        event.target.checked ? '1' : '0'
                      )}
                    />
                  </Grid>
                </Grid>
                {testimonialsGroup.map((item, index) => {
                  const isTouchedRating = touched?.[item]?.[`store_info/premium_plus_testimonails/${item}_testimonail_rating`];
                  const ratingErrorMessage = errors?.[item]?.[`store_info/premium_plus_testimonails/${item}_testimonail_rating`];

                  const isTouchedContent = touched?.[item]?.[`store_info/premium_plus_testimonails/${item}_testimonail_content`];
                  const contentErrorMessage = errors?.[item]?.[`store_info/premium_plus_testimonails/${item}_testimonail_content`];

                  const isTouchedAuthor = touched?.[item]?.[`store_info/premium_plus_testimonails/${item}_testimonail_author`];
                  const authorErrorMessage = errors?.[item]?.[`store_info/premium_plus_testimonails/${item}_testimonail_author`];

                  const isError = ratingErrorMessage || contentErrorMessage || authorErrorMessage;

                  return (
                    <Accordion elevation={0} key={index}>
                      <AccordionSummary expandIcon={<Icon>expand_more</Icon>} className="my-4">
                        <Badge badgeContent={Boolean(isError) ? '!' : null} color="error">
                          <Typography variant="h3" style={{textTransform: 'capitalize'}}>
                            {item} Testimonial:
                          </Typography>
                        </Badge>
                      </AccordionSummary>
                      <AccordionDetails className="flex-column">
                        <Grid container spacing={3} className="mb-8">
                          <Grid item xs={12} sm={2} className="my-2 ml-4">Enable</Grid>
                          <Grid item xs={12} sm={9}>
                            <Switch
                              color="primary"
                              name={`${item}.store_info/premium_plus_testimonails/is_${item}_testimonail_enabled`}
                              checked={values[item]?.[`store_info/premium_plus_testimonails/is_${item}_testimonail_enabled`] == 1}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2} className="my-2 ml-4">Rating</Grid>
                          <Grid item xs={12} sm={9}>
                            <TextField
                              variant="outlined"
                              name={`${item}.store_info/premium_plus_testimonails/${item}_testimonail_rating`}
                              label="Percent"
                              InputProps={{ inputProps: { min: 0, max: 100 } }}
                              type="number"
                              value={values[item]?.[`store_info/premium_plus_testimonails/${item}_testimonail_rating`]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              helperText={isTouchedRating && ratingErrorMessage}
                              error={isTouchedRating && Boolean(ratingErrorMessage)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2} className="ml-4">Content</Grid>
                          <Grid item xs={12} sm={9}>
                            <TextField
                              fullWidth
                              multiline
                              rows={7}
                              variant="outlined"
                              name={`${item}.store_info/premium_plus_testimonails/${item}_testimonail_content`}
                              label="Text"
                              value={values[item]?.[`store_info/premium_plus_testimonails/${item}_testimonail_content`]}
                              onChange={handleChange}
                              inputProps={{
                                className: 'p-0',
                                shrink: true
                              }}
                              onBlur={handleBlur}
                              helperText={isTouchedContent && contentErrorMessage}
                              error={isTouchedContent && Boolean(contentErrorMessage)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2} className="ml-4">Author</Grid>
                          <Grid item xs={12} sm={9}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              name={`${item}.store_info/premium_plus_testimonails/${item}_testimonail_author`}
                              label="Text"
                              value={values[item]?.[`store_info/premium_plus_testimonails/${item}_testimonail_author`]}
                              onChange={handleChange}
                              inputProps={{
                                shrink: true
                              }}
                              onBlur={handleBlur}
                              helperText={isTouchedAuthor && authorErrorMessage}
                              error={isTouchedAuthor && Boolean(authorErrorMessage)}
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Card>
              <div className="flex justify-end">
                <Button
                  variant="contained"
                  color="primary"
                  className="mt-10 mb-5"
                  type="submit"
                >
                  Save Configurations
                </Button>
              </div>
            </form>
          )}
        </Formik>
      }
      <BackdropCircular loading={loading} />
    </div>
  );
};

export default Testimonials;
